import { useEffect, useState, type ReactNode } from "react";
import { trackBikeView } from "@/lib/analytics";
import { BluebirdLayout } from "@/components/bluebird/BluebirdLayout";
import { BreadcrumbJsonLd } from "@/components/shared/json-ld";
import { AiActions } from "@/components/shared/AiActions";
import { SlimFooter, SubMasthead } from "@/components/bluebird/Chrome";
import { A, PhotoSlot } from "@/components/bluebird/primitives";
import { BikeSection, VisitCta } from "@/components/bluebird/Catalog";
import { SavingsCallout } from "@/components/bluebird/SavingsCallout";
import { color, font, maxWidth, radius, space, type } from "@/lib/design-tokens";
import { bikes as copy } from "@/lib/copy/bikes";
import { photosFor } from "@/lib/photos";
import { productName } from "@/lib/product_name";
import { availableVariantImages, availableVariants } from "@/lib/variant_inventory";
import { CatalogNotice, type CatalogStatus } from "@/components/bluebird/CatalogNotice";
import type { Bike } from "@/types/bike";

interface Props {
  bike: Bike;
  related_bikes?: Bike[];
  catalog_status?: CatalogStatus;
}

const money = (n: number) => "$" + Math.round(n).toLocaleString("en-US");

interface GalleryPhoto {
  url: string;
  srcSet?: string;
  altText?: string | null;
  width?: number | null;
  height?: number | null;
  ground?: string | null;
  fit?: "contain" | "cover";
  focalPosition?: string;
  kind?: "studio" | "lifestyle" | "detail" | "shopify";
}

/**
 * The product page.
 *
 * `VisitCta` swaps to a different real action when the bike is gone — never a
 * disabled button wearing a badge.
 */
function BikeShow({ bike, related_bikes = [], catalog_status }: Props) {
  const displayName = productName(bike);
  const variants = bike.variants || [];
  const hasVariantInventory = variants.some((variant) => Number.isFinite(variant.inventoryQuantity));
  const inStockVariants = availableVariants(bike);

  // Ingested manufacturer photography first — these are the brands' own masters,
  // where the Shopify originals are often small (the Tern listings are uniformly
  // 350x233). Falls back to the Shopify gallery, then to the placeholder.
  const photos = photosFor(bike.handle);
  const availableVariantPhotos: GalleryPhoto[] = availableVariantImages(bike).map(({ image, color: colorName }) => ({
      url: image.url,
      altText: image.altText || (colorName ? `${displayName} in ${colorName}` : displayName),
      width: image.width,
      height: image.height,
      fit: "contain" as const,
      kind: "shopify" as const,
    }));
  const lifestyleHero = photos.lifestyleHero;
  const studioPhoto: GalleryPhoto | null = photos.studio || null;
  const remainingLifestylePhotos = (photos.gallery || []).filter(
    (photo) => photo.kind === "lifestyle" && photo.url !== lifestyleHero?.url,
  );
  const localDetailPhotos = (photos.gallery || []).filter((photo) => photo.kind !== "lifestyle");
  const shopifyPhotos: GalleryPhoto[] = [
    ...(bike.images || []).map((photo) => ({ ...photo, kind: "shopify" as const })),
    ...(bike.image_url ? [{
      url: bike.image_url,
      altText: bike.image_alt,
      width: bike.image_width,
      height: bike.image_height,
      fit: "contain" as const,
      kind: "shopify" as const,
    }] : []),
  ];
  const seen = new Set<string>();
  const galleryPhotos = [...remainingLifestylePhotos, studioPhoto, ...localDetailPhotos, ...availableVariantPhotos, ...shopifyPhotos]
    .filter((photo): photo is GalleryPhoto => Boolean(photo?.url))
    .filter((photo) => {
      if (seen.has(photo.url)) return false;
      seen.add(photo.url);
      return true;
    });
  const [selectedPhoto, setSelectedPhoto] = useState(0);
  const hero = galleryPhotos[selectedPhoto] || galleryPhotos[0];
  const showPreviousPhoto = () => setSelectedPhoto((selectedPhoto - 1 + galleryPhotos.length) % galleryPhotos.length);
  const showNextPhoto = () => setSelectedPhoto((selectedPhoto + 1) % galleryPhotos.length);
  // Shopify options list every configured color, including variants with zero
  // inventory. Build the customer-facing option list from variants that are
  // actually available. If an older payload has no inventory quantities, fall
  // back to its option list rather than hiding data we cannot classify.
  const availableValues = new Map<string, string[]>();
  inStockVariants.forEach((variant) => {
    variant.selectedOptions?.forEach((option) => {
      const values = availableValues.get(option.name) || [];
      if (!values.includes(option.value)) values.push(option.value);
      availableValues.set(option.name, values);
    });
  });
  const listedOptions = (bike.options || [])
    .map((option) => ({
      ...option,
      values: hasVariantInventory ? (availableValues.get(option.name) || []) : option.values,
    }))
    .filter(
      (option) => option.name !== "Title" && option.values.length > 0 &&
        !option.values.every((value) => value === "Default Title"),
    );
  const specEntries = Object.entries(bike.specs || {});
  const hasListedPrice = bike.availability_state !== "past_model" && Number.isFinite(bike.price) && bike.price > 0;

  useEffect(() => {
    trackBikeView(bike.handle, bike.brand);
  }, [bike.handle, bike.brand]);

  return (
    <>
      {/* Structured data only — renders no visible markup, so it cannot move a
          pixel in script/design_diff.mjs. Kept from the previous design because
          breadcrumbs are what let a search result show the path to the page. */}
      <BreadcrumbJsonLd
        items={[
          { name: "Bikes", href: "/bikes" },
          { name: bike.title, href: `/bikes/${bike.handle}` },
        ]}
      />

      <SubMasthead
        current="/bikes"
        image={lifestyleHero ? {
          src: lifestyleHero.url,
          srcSet: lifestyleHero.srcSet,
          sizes: "100vw",
          alt: lifestyleHero.altText || `${displayName} in use`,
        } : undefined}
        focalPosition={lifestyleHero?.focalPosition || "center"}
      >
        <div style={{ maxWidth, margin: "0 auto", padding: `28px ${space.gutter} 0` }}>
          <A href="/bikes" className="bb-navlink" style={{ color: color.onBlue, fontSize: "15px" }}>
            {copy.backToAll}
          </A>
        </div>
        <section
          style={{
            maxWidth,
            margin: "0 auto",
            padding: `clamp(24px,4vw,44px) ${space.gutter} clamp(48px,7vw,88px)`,
          }}
        >
          <div style={{ fontSize: "15px", color: color.onBlue, fontWeight: 500 }}>
            {bike.brand_display}
          </div>
          <h1
            style={{
              margin: "10px 0 0",
              fontWeight: 800,
              lineHeight: 1.02,
              // The same headline the Service and About comps carry, so the
              // same token. Was a hand-written clamp(34px,5vw,64px) — off the
              // comp's type scale at both ends.
              fontSize: type.subHero,
              letterSpacing: "-.015em",
              maxWidth: "900px",
            }}
          >
            {bike.title}
          </h1>
          {/* Shopify's inventory can lag behind the floor, so keep a valid
              listed price visible while the availability copy asks visitors
              to confirm with the shop. */}
          {hasListedPrice && (
            <div style={{ marginTop: "18px", fontSize: type.h3, color: color.gold, fontWeight: 700 }}>
              {money(bike.price)}
            </div>
          )}
        </section>
      </SubMasthead>

      {/* The <main> landmark. A screen-reader user's "skip to content" jump
          lands here, and without it the only landmarks on the page are a banner
          and a footer with everything else unlabelled. <main> is display:block
          with no margin, so it wraps the same boxes at the same positions —
          verified by script/design_diff.mjs, which still reports 0%. */}
      <main>
      <CatalogNotice status={catalog_status} />

      <section
        data-section="product"
        style={{
          maxWidth,
          margin: "0 auto",
          padding: `${space.sectionY} ${space.gutter}`,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(min(320px,100%),1fr))",
          gap: "clamp(32px,5vw,72px)",
          alignItems: "start",
        }}
      >
        <div>
          {hero ? (
            <section data-product-gallery="" aria-label={`${displayName} photos`}>
              <div
                style={{
                  aspectRatio: "4/3",
                  borderRadius: radius.card,
                  overflow: "hidden",
                  background: hero.ground || color.surface,
                  position: "relative",
                }}
              >
                <img
                  data-asset-ground={hero.ground ? "" : undefined}
                  src={hero.url}
                  srcSet={hero.srcSet}
                  sizes="(max-width: 800px) 92vw, 560px"
                  alt={hero.altText || displayName}
                  loading="eager"
                  width={hero.width || undefined}
                  height={hero.height || undefined}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: hero.fit || "contain",
                    display: "block",
                  }}
                />
                {galleryPhotos.length > 1 && (
                  <>
                    <button
                      type="button"
                      aria-label="Previous photo"
                      onClick={showPreviousPhoto}
                      style={{
                        position: "absolute",
                        left: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: "44px",
                        height: "44px",
                        borderRadius: "50%",
                        border: `1px solid ${color.border}`,
                        background: "rgba(255,255,255,.94)",
                        color: color.blue,
                        fontSize: "30px",
                        lineHeight: 1,
                        cursor: "pointer",
                        boxShadow: "0 3px 12px rgba(14,35,73,.14)",
                      }}
                    >
                      ‹
                    </button>
                    <button
                      type="button"
                      aria-label="Next photo"
                      onClick={showNextPhoto}
                      style={{
                        position: "absolute",
                        right: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: "44px",
                        height: "44px",
                        borderRadius: "50%",
                        border: `1px solid ${color.border}`,
                        background: "rgba(255,255,255,.94)",
                        color: color.blue,
                        fontSize: "30px",
                        lineHeight: 1,
                        cursor: "pointer",
                        boxShadow: "0 3px 12px rgba(14,35,73,.14)",
                      }}
                    >
                      ›
                    </button>
                    <div
                      aria-live="polite"
                      style={{
                        position: "absolute",
                        right: "12px",
                        bottom: "12px",
                        padding: "5px 9px",
                        borderRadius: "999px",
                        background: "rgba(7,48,112,.88)",
                        color: color.white,
                        fontFamily: font.mono,
                        fontSize: "11px",
                      }}
                    >
                      {selectedPhoto + 1} / {galleryPhotos.length}
                    </div>
                  </>
                )}
              </div>

              {galleryPhotos.length > 1 && (
                <div
                  aria-label="Choose a product photo"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit,minmax(72px,1fr))",
                    gap: "10px",
                    marginTop: "12px",
                  }}
                >
                  {galleryPhotos.map((photo, index) => (
                    <button
                      key={photo.url}
                      type="button"
                      aria-label={`Show ${displayName} photo ${index + 1}`}
                      aria-pressed={selectedPhoto === index}
                      onClick={() => setSelectedPhoto(index)}
                      style={{
                        width: "100%",
                        aspectRatio: "4/3",
                        padding: 0,
                        overflow: "hidden",
                        cursor: "pointer",
                        borderRadius: radius.chip,
                        border: `2px solid ${selectedPhoto === index ? color.blue : color.border}`,
                        background: photo.ground || color.white,
                      }}
                    >
                      <img
                        src={photo.url}
                        alt=""
                        loading="lazy"
                        width={photo.width || undefined}
                        height={photo.height || undefined}
                        style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </section>
          ) : (
            <PhotoSlot
              label={`photo — side profile, ${bike.title}`}
              style={{ aspectRatio: "4/3", borderRadius: radius.card }}
            />
          )}
        </div>

        <div>
          {bike.availability_state !== "available" && (
            <div style={{ marginTop: "10px", fontSize: "17px", fontWeight: 700, color: color.slate }}>
              {bike.availability_state === "past_model" ? copy.pastModelLine : copy.checkAvailabilityLine}
            </div>
          )}
          <VisitCta bike={bike} />
          <div style={{ marginTop: "18px", fontFamily: font.mono, fontSize: "12px", letterSpacing: ".08em", textTransform: "uppercase", color: color.slateLight }}>
            {copy.testRideFacts}
          </div>
          <div style={{ marginTop: "22px" }}>
            <AiActions surface="bike_detail" bike={bike} />
          </div>
          {(specEntries.length > 0 || listedOptions.length > 0 || bike.spec_source) && (
            <section
              aria-labelledby="bike-details-heading"
              style={{ marginTop: "32px", paddingTop: "28px", borderTop: `1px solid ${color.border}` }}
            >
              <h2 id="bike-details-heading" style={{ margin: "0 0 18px", fontSize: type.h3 }}>
                Bike details
              </h2>
              <dl style={{ margin: 0 }}>
                {listedOptions.map((option) => (
                  <div
                    key={option.name}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "minmax(120px, 0.8fr) 1.4fr",
                      gap: "14px",
                      padding: "9px 0",
                      borderBottom: `1px solid ${color.border}`,
                    }}
                  >
                    <dt style={{ color: color.slate, fontWeight: 700 }}>{option.name}</dt>
                    <dd style={{ margin: 0, color: color.inkMuted }}>{option.values.join(", ")}</dd>
                  </div>
                ))}
                {specEntries.map(([label, value]) => (
                  <div
                    key={label}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "minmax(120px, 0.8fr) 1.4fr",
                      gap: "14px",
                      padding: "9px 0",
                      borderBottom: `1px solid ${color.border}`,
                    }}
                  >
                    <dt style={{ color: color.slate, fontWeight: 700 }}>{label}</dt>
                    <dd style={{ margin: 0, color: color.inkMuted }}>{value}</dd>
                  </div>
                ))}
              </dl>
              {bike.spec_source && (
                <p style={{ margin: "16px 0 0", fontSize: "14px", lineHeight: 1.5, color: color.slate }}>
                  Standard model details can change by build. Confirm the exact bike in the shop. {" "}
                  <a
                    href={bike.spec_source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bb-underline-grow"
                    style={{ fontWeight: 700 }}
                  >
                    Full {bike.spec_source.label} specs
                  </a>
                </p>
              )}
            </section>
          )}
          {bike.description_html ? (
            <div
              style={{ marginTop: "28px", fontSize: "16.5px", lineHeight: 1.6, color: color.inkMuted }}
              // Shopify's own product description. It is the vendor's copy, not
              // ours, and it is the only place on this page where text is not
              // written by the shop.
              dangerouslySetInnerHTML={{ __html: bike.description_html }}
            />
          ) : (
            /*
              THE FALLBACK THAT KEEPS THIS PAGE FROM LOOKING BROKEN.

              Three blocks on this page are conditional — the gallery strip
              (needs `images`), this description (needs `description_html`), and
              the related-bikes row (needs a same-brand sibling). A listing that
              has none of them rendered a photograph, a button, and then several
              hundred pixels of nothing before the footer. Aaron screenshotted
              exactly that.

              It is not hypothetical or fixture-only: `description_html` comes
              from Shopify and plenty of real listings leave it blank. The page
              has to read as finished when the vendor supplied nothing, so this
              says what the shop can always say about any bike on the floor.

              Written from what is already true elsewhere on the site — a test
              ride is free and thirty minutes, and a person answers the phone —
              so it invents no fact about this particular bike.
            */
            <div
              style={{ marginTop: "28px", fontSize: "16.5px", lineHeight: 1.6, color: color.inkMuted }}
            >
              {copy.noDescriptionFallback}
            </div>
          )}
        </div>
      </section>

      <SavingsCallout />

      {related_bikes.length > 0 && (
        <BikeSection id="related" heading={related_bikes.every((other) => other.brand === bike.brand) ? (bike.brand_display ? `Also from ${bike.brand_display}` : copy.relatedHeading) : "More bikes to try"} bikes={related_bikes} style={{ paddingTop: space.sectionY }} />
      )}

      </main>

      <SlimFooter borderTop />
    </>
  );
}

BikeShow.layout = (page: ReactNode) => <BluebirdLayout>{page}</BluebirdLayout>;

export default BikeShow;
